import { Pencil } from "lucide-react";
import type { NameChange, Term, TermGroupKind } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { DisclosureChevron, useDisclosure } from "@/components/ui/Disclosure";
import { OriginPill } from "@/components/patterns/OriginPill";
import { SOURCE_LINK } from "@/components/ui/marks";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { toneClasses } from "@/components/ui/tone";
import { stampOf } from "@/lib/day";
import { plural } from "@/lib/plural";
import { cn } from "@/lib/utils";
import { flagOf, type SeenTerms } from "./terms-seen";

/**
 * One term as the Terminology list draws it: a row with its details, or a chip
 * under "Terms only".
 */

const STAGE_LABEL: Record<NameChange["stage"], string> = {
  draft_creation: "New draft",
  intake: "Intake",
  figures: "Figures",
  invention: "Invention",
  novelty: "Novelty",
  claim_strategy: "Claim Strategy",
  specification_plan: "Specification",
  drafting: "Generate",
};

/** The page each group's terms are defined on, as the panel names it. */
export const SURFACE: Record<TermGroupKind, string> = {
  components: "Figures",
  elements: "Invention",
  positions: "Novelty",
};

/** The term the attorney is away editing at its source, ringed in the panel until the trip ends. */
export interface PendingTerm {
  kind: TermGroupKind;
  thingId: string;
}

/** Where the list is drawn: the panel's column, or a card on Generate's review in a grid. */
export type TermsLayout = "dock" | "card";

/** Open a term where it was defined: `edit` to change it there, otherwise only to see it. */
export type GoToSource = (
  kind: TermGroupKind,
  term: Term,
  edit: boolean,
) => void;

/** A term's earlier names, newest first, each with who gave it, where and when. */
function TermHistory({ history }: { history: NameChange[] }) {
  return (
    <ul
      aria-label="Earlier names"
      className="mt-2 flex flex-col gap-1.5 border-t border-dashed border-border pt-2"
    >
      {[...history].reverse().map((change, index) => (
        <li
          key={`${change.name}-${String(index)}`}
          className="flex items-start gap-1.5 text-[11.5px] leading-snug text-muted-foreground"
        >
          <OriginPill origin={change.by} surface="term-history" />
          <span>
            "{change.name}" · {STAGE_LABEL[change.stage]}
            {change.at !== null && ` · ${stampOf(change.at)}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

function mentionsOf(count: number): string {
  return `${plural(count, "mention")} in draft`;
}

/** A term's head line: its numeral, its name, and the way to edit it where it was defined. */
function TermHead({
  term,
  surface,
  onGo,
}: {
  term: Term;
  surface: string;
  onGo: (edit: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2">
      {term.numeral !== null && (
        <StatusPill tone="brand" className="font-mono">
          {term.numeral}
        </StatusPill>
      )}
      <span
        data-term-name
        className={cn(
          "min-w-0 flex-1 text-[13px] leading-snug font-semibold text-pretty",
          !term.named && "font-medium text-muted-foreground italic",
        )}
      >
        {term.named ? term.name : "Not named yet"}
      </span>
      <Button
        variant="link"
        size="dense"
        title={`Edit in ${surface}`}
        onClick={() => {
          onGo(true);
        }}
      >
        <Pencil aria-hidden />
        Edit
      </Button>
    </div>
  );
}

/**
 * The line under an element's or a position's name: the words of the record it
 * is defined on, quoted with that record's name, as v0.29 draws them ("From
 * Element 3: "…""). An element not named yet shows its words there; a term whose
 * words are its name, or a record with none, shows nothing. A component's
 * record holds no words beyond its title.
 */
function sourceQuote(kind: TermGroupKind, term: Term): string | null {
  const words = term.definition?.trim() ?? "";
  if (kind === "components" || words === "") return null;
  if (term.named && words.toLowerCase() === term.name.trim().toLowerCase())
    return null;
  const where = term.source.label.split(" · ")[0];
  return `From ${where}: "${words}"`;
}

/** One term: its numeral, name and the words it is defined by, then where it was defined, who named it and how often the draft uses it. */
function TermRow({
  kind,
  term,
  flag,
  surface,
  pending,
  onGo,
}: {
  kind: TermGroupKind;
  term: Term;
  flag: string | null;
  surface: string;
  pending: boolean;
  onGo: (edit: boolean) => void;
}) {
  const history = useDisclosure();
  const quote = sourceQuote(kind, term);
  return (
    <Panel
      as="li"
      shape="row"
      pad="snug"
      tone={flag === null ? undefined : "warning"}
      data-pending={pending || undefined}
      // The amber ground says the row moved; its words stay in the ink.
      className={cn(
        flag !== null && "text-foreground",
        pending && "border-ring ring-[3px] ring-ring/15",
      )}
    >
      <TermHead term={term} surface={surface} onGo={onGo} />
      {quote !== null && (
        <p className="mt-1 text-[11px] leading-snug text-pretty text-subtle-foreground italic">
          {quote}
        </p>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
        <Button
          variant="link"
          size="dense"
          title={`Open in ${surface}`}
          className={cn("text-muted-foreground", SOURCE_LINK)}
          onClick={() => {
            onGo(false);
          }}
        >
          {term.source.label}
        </Button>
        <OriginPill origin={term.origin} surface="term" />
        {flag !== null && (
          <StatusPill tone="warning" plain>
            {flag}
          </StatusPill>
        )}
        {term.named && (
          <span
            title="Times this term appears in the draft as planned so far"
            className="whitespace-nowrap text-muted-foreground"
          >
            {mentionsOf(term.mentions)}
          </span>
        )}
        {term.history.length > 0 && (
          <Button
            {...history.trigger}
            variant="quiet"
            size="dense"
            className="ml-auto h-auto gap-0.5 px-1"
          >
            <DisclosureChevron open={history.open} className="size-3" />
            Edit history ({term.history.length})
          </Button>
        )}
      </div>
      <div {...history.panel}>
        {history.open && <TermHistory history={term.history} />}
      </div>
    </Panel>
  );
}

/** How the list reads: every term as a chip for a quick scan, or each with its details. */
export type TermsReading = "terms" | "detail";

export const READINGS = [
  { key: "terms", label: "Terms only" },
  { key: "detail", label: "With details" },
] as const;

/** One term as a chip, under Terms only: its numeral, its name, "You" where the attorney named it, and its flag. */
function TermChip({
  term,
  flag,
  onGo,
}: {
  term: Term;
  flag: string | null;
  onGo: () => void;
}) {
  return (
    <li className="max-w-full min-w-0">
      <Button
        variant="secondary"
        size="dense"
        title={`${term.named ? term.name : "Not named yet"} · ${term.source.label}`}
        className={cn(
          // A position is listed by its whole title, so a chip holds to the panel and cuts its name.
          "h-auto max-w-full gap-1.5 rounded-[9px] py-1 pr-2.5 pl-1.5 text-[12.5px] font-semibold",
          term.numeral === null && "pl-2.5",
          flag !== null && toneClasses("warning", "surface"),
          flag !== null && "text-foreground",
        )}
        onClick={onGo}
      >
        {term.numeral !== null && (
          <StatusPill tone="brand" className="font-mono">
            {term.numeral}
          </StatusPill>
        )}
        <span
          className={cn(
            "min-w-0 truncate",
            !term.named && "font-medium italic",
          )}
        >
          {term.named ? term.name : "Not named yet"}
        </span>
        {term.origin === "attorney" && (
          <OriginPill origin="attorney" surface="term-history" />
        )}
        {flag !== null && (
          <StatusPill tone="warning" plain>
            {flag}
          </StatusPill>
        )}
      </Button>
    </li>
  );
}

/** One group's terms, as chips or as rows, while its heading holds it open. */
export function GroupEntries({
  kind,
  terms,
  seen,
  folded,
  panel,
  onGo,
  pending,
  layout,
  reading,
}: {
  kind: TermGroupKind;
  terms: Term[];
  seen: SeenTerms | null;
  folded: boolean;
  /** The fold's panel attributes, so the heading can name what it folds. */
  panel: { id: string; hidden: boolean };
  onGo: GoToSource;
  pending: PendingTerm | null;
  layout: TermsLayout;
  reading: TermsReading;
}) {
  return reading === "terms" ? (
    <ul {...panel} className="flex flex-wrap gap-1.5 px-1">
      {!folded &&
        terms.map((term) => (
          <TermChip
            key={term.thing_id}
            term={term}
            flag={flagOf(kind, term, seen)}
            onGo={() => {
              onGo(kind, term, false);
            }}
          />
        ))}
    </ul>
  ) : (
    <ul
      {...panel}
      className={
        layout === "card"
          ? "grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-1.5"
          : "flex flex-col gap-1.5"
      }
    >
      {!folded &&
        terms.map((term) => (
          <TermRow
            key={term.thing_id}
            kind={kind}
            term={term}
            flag={flagOf(kind, term, seen)}
            surface={SURFACE[kind]}
            pending={
              pending?.kind === kind && pending.thingId === term.thing_id
            }
            onGo={(edit) => {
              onGo(kind, term, edit);
            }}
          />
        ))}
    </ul>
  );
}
